import { useEffect, useLayoutEffect } from 'react';
import Provider from '@components/Provider';
import useClient from '@hooks/useClient';
import {
  localStorage,
  STORAGE_ACTION,
  authStorageClear,
  saveUTMByQueryString
} from '@utils/storage';
import {
  CapaErrorCode,
  useRefreshMutation,
  useFetchMyQuotationUserLazyQuery,
  useFetchQuotationServiceUserRatingQuery
} from '@generated/graphql';
import AppPresenter from './AppPresenter';
import { IClientState } from '@modules/client/types';
import useNoticeSchedule from './useNoticeSchedule';
import debounce from 'lodash/debounce';
import ScrollableModal from '@components/Dialog/ScrollableModal/ScrollableModal';

const AppContainer = () => {
  const [refresh] = useRefreshMutation();
  const [
    fetchMyQuotationUser,
    fetchMyQuotationUserQuery
  ] = useFetchMyQuotationUserLazyQuery({ fetchPolicy: 'network-only' });
  const {
    onInitClient,
    onGetClient,
    onUpdateToken,
    onUpdateClient,
    clientState
  } = useClient();

  const updateModalState = useNoticeSchedule();

  useFetchQuotationServiceUserRatingQuery({
    skip: !clientState.accessToken,
    onCompleted: (result) => {
      if (result?.fetchQuotationServiceUserRating.data?.rated) {
        onUpdateClient({ isEvalutatedCapa: true });
      } else {
        onUpdateClient({ isEvalutatedCapa: false });
      }
    }
  });

  useEffect(() => {
    saveUTMByQueryString();
  }, []);
  /** 자동로그인 처리 */
  useEffect(
    () => {
      const isKeepLoggedIn = localStorage.getItem(
        STORAGE_ACTION.KEEP_LOGGED_IN
      );
      if (isKeepLoggedIn && !clientState.accessToken) {
        refresh({
          variables: { refreshToken: isKeepLoggedIn }
        }).then((result) => {
          const successRefresh =
            result.data?.refresh.result === CapaErrorCode.Success;
          if (successRefresh) {
            const { accessToken, refreshToken } =
              result.data?.refresh.data || {};
            onUpdateToken({
              accessToken: accessToken as string,
              refreshToken: refreshToken as string
            });
            fetchMyQuotationUser();
          }
        });
      }
    },
    //eslint-disable-next-line
    []
  );

  /** 고객 정보 fetch 후 데이터 업데이트 */
  useEffect(
    () => {
      const { data: clientInfo } = fetchMyQuotationUserQuery;
      if (clientInfo) {
        const successFetchMyQuotationUser =
          clientInfo?.fetchMyQuotationUser.result === CapaErrorCode.Success;
        if (successFetchMyQuotationUser) {
          const clientData = clientInfo?.fetchMyQuotationUser.data;
          onGetClient(clientData as Partial<IClientState>);
        } else if (!clientState.accessToken || !clientState.refreshToken) {
          onInitClient();
          authStorageClear();
        }
      }
    },
    //eslint-disable-next-line
    [fetchMyQuotationUserQuery]
  );

  useLayoutEffect(() => {
    let vh = window.innerHeight * 0.01;
    const root = window.document.getElementById('root');

    root?.style.setProperty('--vh', `${vh}px`);

    const resize = debounce(() => {
      vh = window.innerHeight * 0.01;

      root?.style.setProperty('--vh', `${vh}px`);
    }, 250);

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <Provider>
      <ScrollableModal />
      <AppPresenter updateModalState={updateModalState} />
    </Provider>
  );
};

export default AppContainer;
